import {
  AADTType,
  AttributeName,
  CodingAttributes,
  FacilityType,
  GradeType,
  HeavyVehicleFlowType,
  IntersectionApproachType,
  NumberOfLanesType,
  PresenceType,
} from "@keita/lanepatrol-sdk";
import { RootStore } from "../../shared/stores/RootStore";
import { OsaNodeCoding } from "../models/OsaNodeCoding";

export namespace SuggestionConditions {
  export const noAdjacentSidewalk = (attr: CodingAttributes) =>
    attr.facilityType === FacilityType.Sidewalk;

  export const noDelineation = (attr: CodingAttributes) =>
    attr.facilityType === FacilityType.MixedTrafficRoadLane;

  export const noIntersectionOrRoadCrossing = (attr: CodingAttributes) =>
    attr.intersectionOrRoadCrossing === PresenceType.NotPresent;

  export const noAdjacentRoadLane = (attr: CodingAttributes) =>
    (attr.facilityType === FacilityType.OffRoadBicyclePath ||
      attr.facilityType === FacilityType.MultiUsePath ||
      attr.facilityType === FacilityType.Sidewalk) &&
    attr.adjacentFeatures0to1m?.roadLane === PresenceType.NotPresent &&
    attr.adjacentFeatures1to3m?.roadLane === PresenceType.NotPresent;
}

export class SuggestionEngine {
  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
  }

  makeSuggestions(nodeCoding: OsaNodeCoding) {
    const { codingStore } = this.rootStore;
    const { attributes } = nodeCoding;

    if (SuggestionConditions.noAdjacentSidewalk(attributes)) {
      codingStore.suggestValue(
        nodeCoding,
        AttributeName.AdjacentFeatures0to1mSidewalk,
        PresenceType.NotPresent,
        true,
      );
      codingStore.suggestValue(
        nodeCoding,
        AttributeName.AdjacentFeatures1to3mSidewalk,
        PresenceType.NotPresent,
        true,
      );
    }

    if (SuggestionConditions.noDelineation(attributes)) {
      codingStore.suggestValue(
        nodeCoding,
        AttributeName.Delineation,
        PresenceType.NotPresent,
        true,
      );
    }

    if (SuggestionConditions.noIntersectionOrRoadCrossing(attributes)) {
      codingStore.suggestValue(
        nodeCoding,
        AttributeName.IntersectionApproach,
        IntersectionApproachType.SeparateOrNA,
        true,
      );
      codingStore.suggestValue(
        nodeCoding,
        AttributeName.NumberOfLanesIntersectingRoad,
        NumberOfLanesType.OnePerDirection,
        false,
      );
      codingStore.suggestValue(
        nodeCoding,
        AttributeName.CrossingFacility,
        PresenceType.NotPresent,
        true,
      );
    }

    if (SuggestionConditions.noAdjacentRoadLane(attributes)) {
      codingStore.suggestValue(
        nodeCoding,
        AttributeName.NumberOfLanesAdjacentRoad,
        NumberOfLanesType.OnePerDirection,
        false,
      );
      codingStore.suggestValue(
        nodeCoding,
        AttributeName.RoadAADT,
        AADTType.AADT0,
        true,
      );
      codingStore.suggestValue(
        nodeCoding,
        AttributeName.RoadOperatingSpeed,
        0,
        true,
      );
      codingStore.suggestValue(
        nodeCoding,
        AttributeName.RoadSpeedLimit,
        0,
        true,
      );
      codingStore.suggestValue(
        nodeCoding,
        AttributeName.HeavyVehicleFlow,
        HeavyVehicleFlowType.LowOrRestrictedAccess,
        true,
      );
    }
  }

  suggestGradeType(nodeId: string, slope: number) {
    const { codingStore } = this.rootStore;
    const nodeCoding = codingStore.getFromSelectedWay()!.get(nodeId);
    if (nodeCoding) {
      codingStore.suggestValue(
        nodeCoding,
        AttributeName.Grade,
        slope < 5 ? GradeType.LessThan5degrees : GradeType.MoreThan5degrees,
        false,
      );
    }
  }
}
